﻿import { RefObject, useCallback } from 'react';
import { EasingFunction, TransitionDuration } from '../Types';
import { useSliderContext } from '../SliderContext';

type UsePositioningProps = {
  navClickEasingFunction: EasingFunction
  touchEasingFunction: EasingFunction
  touchTransitionDuration: TransitionDuration
  navClickTransitionDuration: TransitionDuration
  groupSize: RefObject<number>
  spaceBetweenTiles: number,
  enableTestAttributes: boolean
}

const usePositioning = (
  {
    navClickTransitionDuration,
    touchEasingFunction,
    touchTransitionDuration,
    enableTestAttributes,
    navClickEasingFunction,
    spaceBetweenTiles,
  }: UsePositioningProps) => {
  const { root, position, parentRect, tiles, groupSize, tileWidth, prefersReducedMotion } = useSliderContext();

  const setEasingFunction = useCallback((type?: 'touch' | 'click' | 'unset') => {
    if (!root.current) return;

    if (!type || type === 'unset') {
      root.current.style.transition = `${type || ''}`;
      return;
    }

    let transitionDuration: TransitionDuration;
    if (prefersReducedMotion) {
      transitionDuration = '0s';
    } else {
      transitionDuration = type === 'click' ? navClickTransitionDuration : touchTransitionDuration;
    }

    const easingFunction = type === 'click' ? navClickEasingFunction : touchEasingFunction;

    root.current.style.transition = `transform ${transitionDuration} ${easingFunction}`;

  }, [root, navClickEasingFunction, navClickTransitionDuration, touchEasingFunction, touchTransitionDuration, prefersReducedMotion]);

  const translate = useCallback((newPosition: number) => {
    if (!root.current) return;

    root.current.style.transform = `translate3d(${newPosition}px,0,0)`;
  }, [root, enableTestAttributes]);

  const snap = useCallback((toEdge: 'left' | 'right' = 'left') => {
    if (!root.current || !parentRect.current || !groupSize.current || !tiles.current) return;

    let snapIndex = 0;

    let closest = Infinity;
    for (let i = 0; i < tiles.current.length; i++) {
      if (i + groupSize.current - 1 > tiles.current.length - 1 && toEdge !== 'right') break;

      const tileDimensions = tiles.current[i]?.getBoundingClientRect();
      if (!tileDimensions) return;

      const distanceToTarget = parentRect.current[toEdge] - tileDimensions[toEdge];

      if (Math.abs(distanceToTarget) < Math.abs(closest)) {
        closest = distanceToTarget;
        snapIndex = i;
      }
    }

    const newPosition = ((snapIndex) * ((tileWidth + spaceBetweenTiles))) * -1;
    translate(newPosition);
    position.current = newPosition;

  }, [root, parentRect, groupSize, tiles, position, tileWidth, spaceBetweenTiles]);

  return {
    snap,
    translate,
    setEasingFunction
  };
};

export default usePositioning;